/**
 * SetVariableField - renders the SetVariable action's `name` + `value` pair.
 *
 * `name` reuses VariableNameField (the project's standard variable picker)
 * so behaviour stays consistent with the rest of the editor. `value` reuses
 * VariableValueInput so the input shape matches InitializeVariableField
 * (boolean Dropdown, multiline JSON for Object/Array, expression preview).
 */

import { useId } from 'react';
import { makeStyles, tokens, Label, Text } from '@coreai/fluentui-react';
import type { ValueSegment } from '@/types/valueSegment';
import type { AgentParameterSchema } from './AgentParameterMenu';
import { useAvailableVariables } from '@/hooks/useAvailableVariables';
import { VariableNameField } from './VariableNameField';
import { VariableValueInput, coerceVariableType, coerceVariableValue } from './VariableValueInput';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  valueField: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },
  valueLabel: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightMedium,
    color: tokens.colorNeutralForeground2,
  },
  required: {
    color: tokens.colorPaletteRedForeground1,
    marginLeft: tokens.spacingHorizontalXXS,
  },
  hint: {
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
  },
});

export interface SetVariableFieldProps {
  nameValue: string | ValueSegment[];
  valueValue: string;
  onNameChange: (next: string | ValueSegment[]) => void;
  /**
   * Receives the value coerced to the matched variable's JS type
   * (boolean / number / object / array). Raw strings are returned
   * unchanged when they aren't a clean literal — this preserves
   * expressions/tokens like `@variables('foo')`.
   */
  onValueChange: (next: string | number | boolean | object) => void;
  description?: string | undefined;
  required?: boolean | undefined;
  disabled?: boolean | undefined;
  error?: string | undefined;
  showAgentParameterButton?: boolean | undefined;
  agentParameterSchema?: AgentParameterSchema | undefined;
  actionOutputs?: Record<string, unknown> | undefined;
  hasRunData?: boolean | undefined;
  isStale?: boolean | undefined;
}

export const SetVariableField = ({
  nameValue,
  valueValue,
  onNameChange,
  onValueChange,
  description,
  required,
  disabled,
  error,
  showAgentParameterButton,
  agentParameterSchema,
  actionOutputs,
  hasRunData,
  isStale,
}: SetVariableFieldProps) => {
  const styles = useStyles();
  const fieldPrefix = useId();
  const variables = useAvailableVariables();
  const selectedName = typeof nameValue === 'string' ? nameValue : '';
  const matched = variables.find((v) => v.name === selectedName);
  const variableType = coerceVariableType(matched?.type);

  return (
    <div className={styles.container}>
      <VariableNameField
        name="name"
        label="Name"
        value={nameValue}
        onChange={onNameChange}
        description={description}
        required={required}
        disabled={disabled}
        error={error}
      />

      <div className={styles.valueField}>
        <Label className={styles.valueLabel}>
          Value
          {required && <span className={styles.required}>*</span>}
        </Label>
        <VariableValueInput
          fieldId={`${fieldPrefix}_value`}
          type={variableType}
          value={valueValue}
          segmentId={`${fieldPrefix}_value_seg`}
          onChange={(raw) => onValueChange(coerceVariableValue(variableType, raw))}
          disabled={disabled}
          showAgentParameterButton={showAgentParameterButton}
          agentParameterSchema={agentParameterSchema}
          actionOutputs={actionOutputs}
          hasRunData={hasRunData}
          isStale={isStale}
        />
        {!matched && selectedName && (
          <Text size={200} className={styles.hint}>
            Unknown variable — value will be saved as a string.
          </Text>
        )}
      </div>
    </div>
  );
};
